import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Jornadas.css';
import { useTemporada } from '../context/TemporadaContext';
import { useAuth } from '../context/AuthContext';
import { useCalendarioDetalle } from '../hooks/useCalendarioDetalle';
import { useFiltroJornadas } from '../hooks/useFiltroJornadas';
import { conArrastrados, esPartidoDeJugador, partidoContieneTexto } from '../utils/jornadasUtils';
import PartidoJornada from './PartidoJornada';
import { usePistasPartidos } from '../hooks/usePistasPartidos';
import { useDatosJornadas } from '../hooks/useDatosJornadas';
import { descargarJornadasExcel, descargarJornadasPDF } from '../utils/descargaJornadas';

const MODALIDADES = [
    'DOBLES FEMENINO',
    'DOBLES MASCULINO',
    'DOBLES MIXTO',
    'INDIVIDUAL FEMENINO',
    'INDIVIDUAL MASCULINO',
];

const capitalize = (s) => s.charAt(0) + s.slice(1).toLowerCase();

const Jornadas = () => {
    const { temporada } = useTemporada();
    const { usuario } = useAuth();
    const location = useLocation();
    const esPrivado = location.pathname.startsWith('/dashboard');
    const basePath = esPrivado ? '/dashboard/jornadas' : '/jornadas';

    const { partidos: partidosCalendario, rosterPorParticipante, cargando, error } = useCalendarioDetalle(temporada);
    // Los aplazados con justificante aprobado también se ven en la jornada siguiente (solo visual).
    const partidos = useMemo(() => conArrastrados(partidosCalendario), [partidosCalendario]);
    // Pista y hora son de la jornada real del partido, no de su copia arrastrada.
    const pistaDe = (p) => (p.arrastradoDe ? undefined : pistasPorPartido[p.id]);
    const {
        soloMios, setSoloMios, puedeFiltrarMios, modalidad, setModalidad, busqueda, setBusqueda,
    } = useFiltroJornadas({ esPrivado, jugadorId: usuario?.jugadorId });

    const term = busqueda.trim();
    const jornadasConPartidos = useMemo(() => [...new Set(partidos.map((p) => p.jornada))].sort((a, b) => a - b), [partidos]);
    const pistasPorPartido = usePistasPartidos(temporada, jornadasConPartidos, !esPrivado);
    const datosPorJornada = useDatosJornadas(temporada);

    const jornadasFiltradas = partidos.filter((j) => {
        if (modalidad !== 'Todos' && j.modalidad !== modalidad) return false;
        if (soloMios) return esPartidoDeJugador(j, usuario.jugadorId, rosterPorParticipante);
        return partidoContieneTexto(j, term, rosterPorParticipante);
    });

    const agrupadas = jornadasFiltradas.reduce((acc, j) => {
        if (!acc[j.jornada]) acc[j.jornada] = [];
        acc[j.jornada].push(j);
        return acc;
    }, {});

    // Por hora y, a igual hora, por pista; sin hora, al final; sort es estable y conserva el orden original
    const porHora = (a, b) => {
        if (!a?.hora) return b?.hora ? 1 : 0;
        if (!b?.hora) return -1;
        return a.hora.localeCompare(b.hora) || (Number(a.pista) - Number(b.pista)) || 0;
    };

    const numeros = Object.keys(agrupadas).map(Number).sort((a, b) => a - b);

    const nombreBase = () => {
        const mod = modalidad === 'Todos' ? 'todas' : modalidad.toLowerCase().replace(/ /g, '-');
        const busq = term ? `_${term.replace(/ /g, '-')}` : '';
        return `jornadas_${temporada}_${mod}${busq}`;
    };

    // Con `num` descarga solo esa jornada (respetando los filtros); sin él, todas las filtradas
    const exportarExcel = (num) => (num == null
        ? descargarJornadasExcel(jornadasFiltradas, temporada, `${nombreBase()}.xlsx`)
        : descargarJornadasExcel(agrupadas[num], temporada, `${nombreBase()}_jornada-${num}.xlsx`));

    const exportarPDF = (num) => (num == null
        ? descargarJornadasPDF(jornadasFiltradas, temporada, `${nombreBase()}.pdf`)
        : descargarJornadasPDF(agrupadas[num], temporada, `${nombreBase()}_jornada-${num}.pdf`));

    return (
        <div className="jornadas-page">
            <h2 className="jornadas-titulo">Temporada {temporada}</h2>

            <div className="modalidad-filtro">
                <label>
                    <input type="radio" name="modalidad" value="Todos" onChange={() => setModalidad('Todos')} checked={modalidad === 'Todos'} />
                    Todas
                </label>
                {MODALIDADES.map(m => (
                    <label key={m}>
                        <input type="radio" name="modalidad" value={m} onChange={() => setModalidad(m)} checked={modalidad === m} />
                        {capitalize(m)}
                    </label>
                ))}
            </div>

            {puedeFiltrarMios && (
                <label className="jornadas-toggle-mios">
                    <input
                        type="checkbox"
                        checked={!soloMios}
                        onChange={(e) => setSoloMios(!e.target.checked)}
                    />
                    Ver todos los partidos
                </label>
            )}

            <div className="jornadas-toolbar">
                {!soloMios && (
                    <input
                        type="search"
                        placeholder="Buscar participante..."
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                        className="jornadas-busqueda-input"
                    />
                )}
                <div className="jornadas-acciones">
                    <button
                        className="jornadas-btn-descarga"
                        onClick={() => exportarExcel()}
                        disabled={!jornadasFiltradas.length}
                        title="Descargar Excel"
                    >
                        Excel
                    </button>
                    <button
                        className="jornadas-btn-descarga"
                        onClick={() => exportarPDF()}
                        disabled={!jornadasFiltradas.length}
                        title="Descargar PDF"
                    >
                        PDF
                    </button>
                </div>
            </div>

            {cargando && <p className="jornadas-mensaje">Cargando...</p>}
            {error && <p className="jornadas-mensaje jornadas-error">{error}</p>}
            {!cargando && !error && numeros.length === 0 && (
                <p className="jornadas-mensaje">
                    {soloMios ? 'No tienes partidos con este filtro.' : 'No hay jornadas para esta temporada.'}
                </p>
            )}

            {numeros.map(num => (
                <div key={num} className="ronda">
                    <h4 className="ronda-cabecera">
                        <span className="ronda-titulo">
                            <Link to={`${basePath}/${num}`} className="ronda-enlace">Jornada {num}</Link>
                            {datosPorJornada[num]?.fecha && <span className="ronda-fecha">{datosPorJornada[num].fecha}</span>}
                        </span>
                        <span className="jornadas-acciones">
                            <button className="jornadas-btn-descarga jornadas-btn-descarga--mini" onClick={() => exportarExcel(num)} title={`Descargar Excel de la jornada ${num}`}>Excel</button>
                            <button className="jornadas-btn-descarga jornadas-btn-descarga--mini" onClick={() => exportarPDF(num)} title={`Descargar PDF de la jornada ${num}`}>PDF</button>
                        </span>
                    </h4>
                    {[...agrupadas[num]].sort((a, b) => porHora(pistaDe(a), pistaDe(b))).map(partido => (
                        <PartidoJornada key={`${partido.id}-${partido.arrastradoDe ?? ''}`} partido={partido} pista={pistaDe(partido)} sede={datosPorJornada[num]?.sede} />
                    ))}
                </div>
            ))}
        </div>
    );
};

export default Jornadas;
